import { useCallback, useEffect, useState } from "react";
import { Loader2, LogOut, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/Ui";
import ManifestDialog from "@/pages/admin/ManifestDialog";
import { api } from "@/lib/api";
import { fmtTime, todayStr, statusStyle } from "@/lib/format";
import { useAuth, userRoles } from "@/context/AuthContext";

const isOpenTrip = (t) => t.status === "scheduled" || t.status === "boarding";
const nowStr = () => new Date().toISOString().slice(0, 16);

export default function SettlementsAdmin({ lists }) {
  const { user } = useAuth();
  const roles = userRoles(user);
  const privileged = roles.includes("admin") || roles.includes("manager");
  const smLocked = (roles.includes("station_master") || roles.includes("teller")) && !privileged && !!user?.station_id;
  const [date, setDate] = useState(todayStr());
  const [status, setStatus] = useState("pending");
  const [station, setStation] = useState(smLocked ? user.station_id : "all");
  const [trips, setTrips] = useState(null);
  const [manifest, setManifest] = useState(null);
  const load = useCallback(() => api.get("/admin/trips", { params: { date } }).then((r) => setTrips(r.data)), [date]);
  useEffect(() => { setTrips(null); load(); }, [load]);
  const matchStatus = (t) => status === "all" ? true : status === "pending" ? isOpenTrip(t) : t.status === "departed";
  const shown = (trips || []).filter((t) => (station === "all" || t.origin_id === station) && matchStatus(t));
  const stations = lists?.stations || [];
  return (
    <div className="space-y-4" data-testid="signout-bus-admin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-2xl font-extrabold tracking-tight">Sign-out Bus</h2>
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList className="rounded-xl">
            <TabsTrigger value="pending" data-testid="signout-tab-pending">Pending</TabsTrigger>
            <TabsTrigger value="disbursed" data-testid="signout-tab-disbursed">Disbursed</TabsTrigger>
            <TabsTrigger value="all" data-testid="signout-tab-all">All</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <div className="card-soft grid gap-3 p-3 sm:grid-cols-2 lg:grid-cols-[220px_160px] lg:items-center" data-testid="signout-filters">
        <Select value={station} onValueChange={setStation} disabled={smLocked}>
          <SelectTrigger data-testid="signout-station-select"><SelectValue /></SelectTrigger>
          <SelectContent>{!smLocked && <SelectItem value="all">All stations</SelectItem>}{stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.city} - {s.name}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="signout-date-input" />
      </div>
      {trips === null && <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#0052FF]" />}
      {trips && shown.length === 0 && <p className="card-soft p-6 text-center text-sm text-slate-500" data-testid="signout-empty">No buses to sign out here.</p>}
      <div className="space-y-2">
        {shown.map((t) => (
          <div key={t.id} className="card-soft flex flex-wrap items-center gap-3 p-4" data-testid={`signout-row-${t.id}`}>
            <div className="w-16 font-display text-xl font-extrabold">{fmtTime(t.departure)}</div>
            <div className="min-w-[200px] flex-1">
              <div className="font-display font-bold">{t.origin_city} → {t.destination_city}</div>
              <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                <span>{t.bus_type}</span>
                {t.bus_assigned ? <span>· Bus {t.bus_number}</span> : <span className="chip bg-amber-50 text-amber-700">Bus not assigned</span>}
                {t.driver_assigned ? <span>· Driver {t.driver_name}</span> : <span className="chip bg-amber-50 text-amber-700">Driver not assigned</span>}
              </div>
            </div>
            <div className="w-44" data-testid={`signout-seats-${t.id}`}>
              <div className="mb-1 text-xs font-semibold text-slate-600">Seats {t.capacity} · Booked {t.booked} · Free {Math.max(0, t.capacity - t.booked)}</div>
              <Progress value={(t.booked / (t.capacity || 1)) * 100} className="h-2" />
            </div>
            <StatusBadge status={t.status} cls={statusStyle[t.status]} />
            {isOpenTrip(t) && t.departure < nowStr() && <span className="chip bg-rose-100 text-rose-700" data-testid={`signout-due-${t.id}`}>Sign-out due</span>}
            <Button size="sm" className={`rounded-full ${isOpenTrip(t) ? "bg-emerald-600 hover:bg-emerald-700" : "bg-[#0052FF] hover:bg-[#0041CC]"}`} onClick={() => setManifest(t)} data-testid={`signout-action-${t.id}`}>
              {isOpenTrip(t) ? <><LogOut className="h-4 w-4" />Sign out</> : <><FileText className="h-4 w-4" />Account & disburse</>}
            </Button>
          </div>
        ))}
      </div>
      <ManifestDialog trip={manifest} onClose={() => setManifest(null)} onChanged={load} />
    </div>
  );
}
